/**
 * @fileoverview Configurable dashboard filter controls shared by tenant and system dashboards.
 * @module ui-admin/components/dashboard/GlobalFilterWidget
 *
 * Responsibilities:
 * - Render dropdown, date-range, search, and multi-select dashboard filters.
 * - Convert selected values into the shared filter-value contract for the host.
 * Dependencies: React, lucide-react, DashboardWidget.
 * Architecture: Presentation component; widget configuration and current values are supplied by props.
 * Notes: Filter changes are emitted to the host and are not persisted here.
 */
// ---------------------------------------------------------------------------
// Global Filter Widget — Filter control that affects all dashboard widgets
// Shared component for tenant-web and system-web
// ---------------------------------------------------------------------------

import { useState, useEffect, useMemo } from 'react';
import { Filter, Calendar, ChevronDown, X, Search } from 'lucide-react';
import type { DashboardWidget } from '@firesquirrel/local-db';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * @contract FilterValue
 * Purpose: Represents a dashboard predicate emitted by a filter control.
 * Fields: field, operator, value.
 * Rules: value shape must match its operator (for example, between uses a two-item range).
 * Sync: Passed to the dashboard host; this component does not persist filter state.
 */
export interface FilterValue {
  field: string;
  operator: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'in' | 'between';
  value: string | number | string[] | [string, string];
}

/**
 * @contract GlobalFilterConfig
 * Purpose: Defines the control and field presented by a dashboard filter widget.
 * Fields: filterType, field, label, options, defaultValue, placeholder.
 * Rules: options apply to dropdown and multi-select controls.
 * Sync: Configuration is read from the supplied dashboard widget.
 */
export interface GlobalFilterConfig {
  filterType: 'dropdown' | 'dateRange' | 'search' | 'multiSelect';
  field: string;
  label: string;
  options?: { value: string; label: string }[];
  defaultValue?: string | string[];
  placeholder?: string;
}

interface GlobalFilterWidgetProps {
  widget: DashboardWidget;
  onFilterChange: (filterId: string, value: FilterValue | null) => void;
  currentValue?: FilterValue | null;
  lookupOptions?: Array<{ value: string; label: string }>;
}

// ---------------------------------------------------------------------------
// Date Range Presets
// ---------------------------------------------------------------------------

const DATE_PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: 'last7days' },
  { label: 'Last 30 Days', value: 'last30days' },
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'This Quarter', value: 'thisQuarter' },
  { label: 'This Year', value: 'thisYear' },
  { label: 'Custom Range', value: 'custom' },
];

function getDateRangeFromPreset(preset: string): [string, string] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (preset) {
    case 'today':
      return [today.toISOString(), new Date(today.getTime() + 86400000 - 1).toISOString()];
    case 'yesterday': {
      const yesterday = new Date(today.getTime() - 86400000);
      return [yesterday.toISOString(), new Date(today.getTime() - 1).toISOString()];
    }
    case 'last7days': {
      const start = new Date(today.getTime() - 7 * 86400000);
      return [start.toISOString(), now.toISOString()];
    }
    case 'last30days': {
      const start = new Date(today.getTime() - 30 * 86400000);
      return [start.toISOString(), now.toISOString()];
    }
    case 'thisMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return [start.toISOString(), now.toISOString()];
    }
    case 'lastMonth': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      return [start.toISOString(), end.toISOString()];
    }
    case 'thisQuarter': {
      const quarter = Math.floor(now.getMonth() / 3);
      const start = new Date(now.getFullYear(), quarter * 3, 1);
      return [start.toISOString(), now.toISOString()];
    }
    case 'thisYear': {
      const start = new Date(now.getFullYear(), 0, 1);
      return [start.toISOString(), now.toISOString()];
    }
    default:
      return [today.toISOString(), now.toISOString()];
  }
}

// ---------------------------------------------------------------------------
// Dropdown Filter
// ---------------------------------------------------------------------------

function DropdownFilter({
  config,
  value,
  onChange,
}: {
  config: GlobalFilterConfig;
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const options = config.options || [];
  const selectedOption = options.find((o) => o.value === value);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
          {selectedOption?.label || config.placeholder || 'Select...'}
        </span>
        <div className="flex items-center gap-1">
          {value && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              className="p-0.5 hover:bg-gray-200 rounded-sm"
            >
              <X className="h-3 w-3 text-gray-400" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 transition-colors ${
                  option.value === value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Date Range Filter
// ---------------------------------------------------------------------------

function DateRangeFilter({
  config,
  value,
  onChange,
}: {
  config: GlobalFilterConfig;
  value: [string, string] | null;
  onChange: (value: [string, string] | null) => void;
}) {
  const [preset, setPreset] = useState<string>('last30days');
  const [isOpen, setIsOpen] = useState(false);

  const handlePresetChange = (newPreset: string) => {
    setPreset(newPreset);
    if (newPreset !== 'custom') {
      onChange(getDateRangeFromPreset(newPreset));
    }
    setIsOpen(false);
  };

  const selectedPreset = DATE_PRESETS.find((p) => p.value === preset);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-gray-900">{selectedPreset?.label || 'Select date range'}</span>
        </div>
        <div className="flex items-center gap-1">
          {value && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
                setPreset('');
              }}
              className="p-0.5 hover:bg-gray-200 rounded-sm"
            >
              <X className="h-3 w-3 text-gray-400" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.value}
                onClick={() => handlePresetChange(p.value)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 transition-colors ${
                  p.value === preset ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Search Filter
// ---------------------------------------------------------------------------

function SearchFilter({
  config,
  value,
  onChange,
}: {
  config: GlobalFilterConfig;
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const [inputValue, setInputValue] = useState(value || '');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue !== value) {
        onChange(inputValue || null);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, value, onChange]);

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder={config.placeholder || 'Search...'}
        className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      {inputValue && (
        <button
          onClick={() => {
            setInputValue('');
            onChange(null);
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-sm"
        >
          <X className="h-3 w-3 text-gray-400" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Multi-Select Filter
// ---------------------------------------------------------------------------

function MultiSelectFilter({
  config,
  value,
  onChange,
}: {
  config: GlobalFilterConfig;
  value: string[] | null;
  onChange: (value: string[] | null) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const options = config.options || [];
  const selectedValues = value || [];

  const toggleOption = (optionValue: string) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];
    onChange(newValues.length > 0 ? newValues : null);
  };

  const selectedLabels = options
    .filter((o) => selectedValues.includes(o.value))
    .map((o) => o.label)
    .join(', ');

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
      >
        <span className={selectedLabels ? 'text-gray-900 truncate' : 'text-gray-500'}>
          {selectedLabels || config.placeholder || 'Select...'}
        </span>
        <div className="flex items-center gap-1">
          {selectedValues.length > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-sm">
              {selectedValues.length}
            </span>
          )}
          {selectedValues.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              className="p-0.5 hover:bg-gray-200 rounded-sm"
            >
              <X className="h-3 w-3 text-gray-400" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
            {options.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => toggleOption(option.value)}
                  className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Global Filter Widget
// ---------------------------------------------------------------------------

/**
 * @component GlobalFilterWidget
 * Purpose: Render the filter control configured for a dashboard widget.
 * Props: Dashboard widget, current filter value, and filter-change callback.
 * Behavior: Selects a filter UI by config and emits the corresponding operator/value.
 * Data Source: local (widget configuration and filter state supplied by props).
 * Constraints: The parent owns applying, synchronizing, and persisting filters.
 * Performance: Memoizes normalized widget configuration.
 */
export function GlobalFilterWidget({
  widget,
  onFilterChange,
  currentValue,
  lookupOptions,
}: GlobalFilterWidgetProps) {
  const config: GlobalFilterConfig = useMemo(() => {
    const typedCfg = widget.filterConfig;
    const widgetConfig = (widget as any).config || {};
    const mappedFilterType: GlobalFilterConfig['filterType'] =
      typedCfg?.type === 'dateRange'
        ? 'dateRange'
        : typedCfg?.type === 'text'
          ? 'search'
          : typedCfg?.type === 'select'
            ? 'dropdown'
            : widgetConfig.filterType || 'dropdown';
    return {
      filterType: mappedFilterType,
      field: typedCfg?.field || widgetConfig.field || 'status',
      label: typedCfg?.label || widgetConfig.label || widget.title || 'Filter',
      options: lookupOptions || widgetConfig.options || [
        { value: 'active', label: 'Active' },
        { value: 'pending', label: 'Pending' },
        { value: 'closed', label: 'Closed' },
      ],
      placeholder: widgetConfig.placeholder,
      defaultValue: widgetConfig.defaultValue,
    };
  }, [widget, lookupOptions]);

  const handleChange = (value: string | string[] | [string, string] | null) => {
    if (value === null) {
      onFilterChange(widget.id, null);
      return;
    }

    let filterValue: FilterValue;
    
    if (config.filterType === 'dateRange' && Array.isArray(value) && value.length === 2) {
      filterValue = {
        field: config.field,
        operator: 'between',
        value: value as [string, string],
      };
    } else if (config.filterType === 'multiSelect' && Array.isArray(value)) {
      filterValue = {
        field: config.field,
        operator: 'in',
        value: value as string[],
      };
    } else if (config.filterType === 'search') {
      filterValue = {
        field: config.field,
        operator: 'contains',
        value: value as string,
      };
    } else {
      filterValue = {
        field: config.field,
        operator: 'eq',
        value: value as string,
      };
    }

    onFilterChange(widget.id, filterValue);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <Filter className="h-4 w-4 text-gray-500" />
        <span className="flex-1 min-w-0 text-xs font-medium text-gray-600 uppercase tracking-wider">
          {config.label}
        </span>
      </div>

      {config.filterType === 'dropdown' && (
        <DropdownFilter
          config={config}
          value={currentValue?.value as string | null}
          onChange={handleChange}
        />
      )}

      {config.filterType === 'dateRange' && (
        <DateRangeFilter
          config={config}
          value={currentValue?.value as [string, string] | null}
          onChange={handleChange}
        />
      )}

      {config.filterType === 'search' && (
        <SearchFilter
          config={config}
          value={currentValue?.value as string | null}
          onChange={handleChange}
        />
      )}

      {config.filterType === 'multiSelect' && (
        <MultiSelectFilter
          config={config}
          value={currentValue?.value as string[] | null}
          onChange={handleChange}
        />
      )}
    </div>
  );
}

export default GlobalFilterWidget;
